import React, { useEffect, useRef, useState } from 'react';
import {
  FIND_POLL_FOR_MS, FIND_POLL_MS, LOC_FOLLOW_MS, LOC_WAIT_MS, findFromPending, findProgressText, loadFind,
} from '../utils/beacons';
import { useFeatures } from '../utils/features';
import BeaconLocation from './BeaconLocation';

/** A find whose location still waits for the beacon's post-Find scan (jobq-api find_location_view). */
const awaitingScan = (p) => !!(p && p.location && p.location.awaiting_scan);

/**
 * Follow one Find while it lands (jobq-api GET /beacon_finds/{id}): re-read every FIND_POLL_MS
 * until it is lit / failed / expired / replaced, at most FIND_POLL_FOR_MS. `find` = the POST's
 * answer ({command_id, beacon_id, progress}) or findFromPending(beacon). Returns the latest
 * progress (null before the first answer). A new `find` object starts over, also for the same
 * command_id: Find clicked again gets the same find back with its time renewed, so following
 * it starts again too. When it stops before the find finished, the progress says why: phase
 * 'gone' (a 404: JobQ doesn't know it) or 'stale' (followed for FIND_POLL_FOR_MS); both are
 * terminal so the Find button frees up.
 *
 * The shop map (2026-10-20): a beacon on firmware 0.8+ scans the anchors when it is found, and the
 * gateway reports that scan a little after the LED lit. While the answer's `location.awaiting_scan`
 * is true the hook keeps re-reading AFTER the find is terminal, every LOC_FOLLOW_MS for at most
 * LOC_WAIT_MS, so the map moves to "now". The progress stays terminal throughout (the Find button
 * is free, onSettled has fired once); only `location` changes.
 */
export function useFindProgress(find) {
  const commandId = find ? find.command_id : null;
  const [progress, setProgress] = useState(find ? find.progress || null : null);

  useEffect(() => {
    const initial = find ? find.progress || null : null;
    setProgress(initial);
    if (!commandId) return undefined;
    let stopped = false;
    let timer = null;
    let last = initial;
    let followFrom = null;
    const started = Date.now();
    const cleanup = () => {
      stopped = true;
      clearTimeout(timer);
    };
    const stop = (phase) => setProgress({ ...(last || {}), command_id: commandId,
      beacon_id: (last && last.beacon_id) || find.beacon_id, phase, terminal: true });
    // The find is over; its location still waits for the beacon's scan: read again, a while longer.
    const follow = () => {
      if (followFrom === null) followFrom = Date.now();
      if (Date.now() - followFrom >= LOC_WAIT_MS) return;
      timer = setTimeout(tick, LOC_FOLLOW_MS);
    };
    const tick = async () => {
      const finished = !!(last && last.terminal);
      const r = await loadFind(commandId);
      if (stopped) return;
      if (finished) {               // only the location is followed now: never 'gone' / 'stale'
        if (r.ok && r.body) {
          last = r.body;
          setProgress(r.body);
          if (!awaitingScan(r.body)) return;
        } else if (r.status === 404) {
          return;
        }
        follow();
        return;
      }
      if (r.status === 404) {
        stop('gone');
        return;
      }
      if (r.ok && r.body) {
        last = r.body;
        setProgress(r.body);
        if (r.body.terminal) {
          if (awaitingScan(r.body)) follow();
          return;
        }
      }
      if (Date.now() - started >= FIND_POLL_FOR_MS) {
        stop('stale');
        return;
      }
      timer = setTimeout(tick, FIND_POLL_MS);
    };
    if (initial && initial.terminal) {
      if (awaitingScan(initial)) follow();
      return cleanup;
    }
    // no answer yet (a find picked up from a list): read it now, else after one interval
    timer = setTimeout(tick, initial ? FIND_POLL_MS : 0);
    return cleanup;
  }, [commandId, find]);

  return progress;
}

/**
 * One line saying where a Find stands: "Waiting for a gateway to hear BCN-003…", "Sent to
 * laser-dock: connecting…", "BCN-003 is flashing…", or why it didn't, with Try again when it
 * failed or expired (onRetry). onChange(progress) tells the parent (to keep its Find button
 * disabled while busy); onSettled() once it can't change any more (to re-read a list).
 *
 * With GET /features pallet_beacons.location on and a `location` on the progress, the shop map
 * (BeaconLocation: caption, heat, zoom) sits under the line. That is every Find in the app: the
 * Beacons page row, the phone Beacons screen, the TRK popup's BeaconCard and Laser Find Material.
 * `clearMapAfterMs` (the Beacons page: LOC_WAIT_MS) takes the map away that long after the find
 * finished (the line stays); a new Find brings it back.
 */
export default function FindProgress({ find, beaconId = null, onRetry = null, onChange = null, onSettled = null,
  clearMapAfterMs = null, className = '', style = undefined }) {
  const progress = useFindProgress(find);
  const features = useFeatures();
  const words = findProgressText(progress, beaconId || (find && find.beacon_id));
  const settledFor = useRef(null);
  const [mapClearedFor, setMapClearedFor] = useState(null); // the `find` whose map was taken away
  const finished = !!(find && progress && progress.terminal && progress.command_id === find.command_id);

  useEffect(() => {
    if (!(clearMapAfterMs > 0) || !finished) return undefined;
    const t = setTimeout(() => setMapClearedFor(find), clearMapAfterMs);
    return () => clearTimeout(t);
  }, [clearMapAfterMs, finished, find]);

  useEffect(() => {
    if (onChange) onChange(progress);
    const key = progress ? `${progress.command_id}:${progress.phase}` : null;
    if (progress && progress.terminal && settledFor.current !== key) {
      settledFor.current = key;
      if (onSettled) onSettled(progress);
    }
    // the parent's callbacks are not dependencies: only a new answer is news
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  if (!find || !find.command_id || !words.text) return null;
  const showLocation = !!(features.palletBeacons.location && progress && progress.location && mapClearedFor !== find);
  const line = (extraClass, lineStyle) => (
    <div className={`small text-${words.tone} ${extraClass}`.trim()} style={lineStyle} role="status"
      data-testid="find-progress" data-phase={progress ? progress.phase : 'sending'}>
      {words.busy && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
      {words.text}
      {words.retry && onRetry && (
        <button type="button" className="btn btn-link btn-sm p-0 ms-2 align-baseline" onClick={onRetry}>Try again</button>
      )}
    </div>
  );
  if (!showLocation) return line(className, style);
  return (
    <div className={className} style={style} data-testid="find-progress-box">
      {line('', undefined)}
      <BeaconLocation className="mt-2" beaconId={progress.beacon_id || (find && find.beacon_id) || beaconId}
        location={progress.location} />
    </div>
  );
}

/**
 * The Finds a list of beacons is following (the Beacons page, the phone Beacons screen, a lot's
 * beacon card): beacon_id -> the find to show. A find already landing when the list loads (a
 * reload, another device, Laser Find Material) is picked up from the beacon's pending_find; a
 * newer one replaces it. A finished find stays shown until the next Find. busy(id): its Find
 * button stays disabled while the find lands (a second click would get the same find back anyway).
 */
export function useBeaconFinds(beacons) {
  const [finds, setFinds] = useState({});
  const [states, setStates] = useState({});

  useEffect(() => {
    if (!Array.isArray(beacons)) return;
    setFinds((cur) => {
      let next = cur;
      for (const b of beacons) {
        const pf = findFromPending(b);
        if (pf && (!next[b.beacon_id] || next[b.beacon_id].command_id !== pf.command_id)) {
          if (next === cur) next = { ...cur };
          next[b.beacon_id] = pf;
        }
      }
      return next;
    });
  }, [beacons]);

  const start = (beaconId, find) => setFinds((f) => ({ ...f, [beaconId]: find }));
  const track = (beaconId) => (progress) => setStates((s) => (s[beaconId] === progress ? s : { ...s, [beaconId]: progress }));
  const busy = (beaconId) => {
    const f = finds[beaconId];
    if (!f) return false;
    const st = states[beaconId];
    return !(st && st.command_id === f.command_id && st.terminal);
  };
  return { finds, start, track, busy };
}
